import { QUALIFYING } from "../constants/TournamentTypeConstants";
import PositionConstants from "../constants/PositionConstants";
import MainRosterApi from "../httpClients/MainRosterApi";
import QualifyingRosterApi from "../httpClients/QualifyingRosterApi";
import { sortPlayers } from "../components/roster/RosterSortHelper";

/**
 * Converts a list of players to the roster update format expected by the API
 * @param {Array} players - List of player objects
 * @returns {Array} List of roster update objects { profileId, playerPositionCategoryIndex }
 */
export const playersToRosterUpdates = players => {
  const rosterUpdates = [];
  if (players) {
    players.forEach((player, i) => {
      rosterUpdates.push({ profileId: player.profileId, playerPositionCategoryIndex: i });
    });
  }
  return rosterUpdates;
};

/**
 * Loads roster data for a given team and tournament type
 * @param {string} tournamentType - The tournament type (QUALIFYING or MAIN)
 * @param {number} fedTeamId - The federation team ID
 * @param {function} rosterDispatch - The roster context dispatch function
 * @returns {Promise} Promise that resolves when roster is loaded
 */
export const loadRoster = (tournamentType, fedTeamId, rosterDispatch) => {
  const api = tournamentType === QUALIFYING ? QualifyingRosterApi : MainRosterApi;

  if (!fedTeamId) {
    return Promise.resolve();
  }

  // load rosters
  const promises = [
    api.getAvailableRoster(fedTeamId),
    api.getProvisionalRoster(fedTeamId),
    api.getFinalRoster(fedTeamId)
  ];

  return Promise.all(promises).then(([available, provisional, final]) => {
    const { players: finalRoster } = final;

    // Create a Set of profile IDs in final roster for fast lookup
    const finalRosterProfileIds = new Set(finalRoster.map(p => p.profileId));
    // Filter out provisional players that are already in final roster, then concat final roster
    const provisionalOnlyPlayers = provisional.players
      .filter(p => !finalRosterProfileIds.has(p.profileId))
      .map(p => ({ ...p, inFinalRoster: false }));

    const provisionalWithFinal = provisionalOnlyPlayers.concat(finalRoster);

    rosterDispatch({
      type: "setRoster",
      available: sortPlayers("lastName", "ASC", available),
      provisional: sortPlayers("lastName", "ASC", provisionalWithFinal),
      final: {
        catchers: sortPlayers(
          "positionCategoryIndex",
          "ASC",
          finalRoster.filter(p => p.positionCategory === PositionConstants.PLAYER.CATCHER)
        ),
        pitchers: sortPlayers(
          "positionCategoryIndex",
          "ASC",
          finalRoster.filter(p => p.positionCategory === PositionConstants.PLAYER.PITCHER)
        ),
        infield: sortPlayers(
          "positionCategoryIndex",
          "ASC",
          finalRoster.filter(p => p.positionCategory === PositionConstants.PLAYER.INFIELD)
        ),
        outfield: sortPlayers(
          "positionCategoryIndex",
          "ASC",
          finalRoster.filter(p => p.positionCategory === PositionConstants.PLAYER.OUTFIELD)
        )
      }
    });
  });
};

/**
 * Updates tournament roster (provisional and optionally final rosters)
 * @param {string} tournamentType - Tournament type (QUALIFYING or MAIN)
 * @param {number} fedTeamId - Federation team ID
 * @param {Object} provisional - Provisional roster object with players array
 * @param {Object|null} final - Optional final roster object with catchers, pitchers, infield, outfield arrays
 * @returns {Promise} Promise from API call
 */
export const updateTournamentRoster = (tournamentType, fedTeamId, provisional, final = null) => {
  const rosterUpdates = {
    provisionalRoster: playersToRosterUpdates(provisional.players.filter(p => !p.inFinalRoster))
  };

  // Only add final roster updates if provided
  if (final) {
    const { catchers, pitchers, infield, outfield } = final;
    rosterUpdates.catchers = playersToRosterUpdates(catchers);
    rosterUpdates.pitchers = playersToRosterUpdates(pitchers);
    rosterUpdates.infield = playersToRosterUpdates(infield);
    rosterUpdates.outfield = playersToRosterUpdates(outfield);
  }

  const api = tournamentType === QUALIFYING ? QualifyingRosterApi : MainRosterApi;
  return api.updateTournamentRoster(fedTeamId, rosterUpdates);
};
